import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const CHAPTERS = [
  {
    n: "01",
    title: "Preserve the record.",
    body: "Every sunrise, every glacier, every migratory flight. NaturePulse is building the largest continuously growing environmental archive on Earth — one observation at a time.",
  },
  {
    n: "02",
    title: "Document the change.",
    body: "Temperature. Rainfall. Snowfall. Storms. Species. Sea level. We track how the planet is shifting — daily, seasonally, and across decades — with data anyone can read.",
  },
  {
    n: "03",
    title: "Empower the witness.",
    body: "Citizens are climate scientists. Upload wildlife, blooms, storms, floods, and auroras. Every observation joins a permanent scientific archive for future generations.",
  },
  {
    n: "04",
    title: "Prepare the future.",
    body: "By remembering the past, we prepare communities for what comes next — heat waves, floods, wildfires, cyclones. History becomes resilience.",
  },
  {
    n: "05",
    title: "Inform, not advocate.",
    body: "NaturePulse presents scientifically supported information — from IPCC, NASA, WMO, NOAA, IUCN — in a balanced, accessible way. The archive tells the story.",
  },
];

function Chapter({ ch, idx }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.15, 1, 1, 0.2]);

  return (
    <motion.div ref={ref} style={{ opacity }} className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 py-24 md:py-40 border-b border-white/10 last:border-0" data-testid={`chapter-${ch.n}`}>
      <motion.div style={{ y }} className="md:col-span-4 md:sticky md:top-32 self-start">
        <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-4">Chapter</div>
        <div className="font-serif-display text-[18vw] md:text-[12vw] leading-none tracking-tighter text-[color:var(--np-live)]">
          {ch.n}
        </div>
      </motion.div>
      <div className="md:col-span-7 md:col-start-6 md:pt-16">
        <h3 className="font-serif-display font-light text-5xl md:text-7xl leading-[0.95] tracking-tighter mb-8">
          {ch.title}
        </h3>
        <p className="text-[color:var(--np-text-2)] text-lg md:text-xl max-w-2xl leading-relaxed">{ch.body}</p>
      </div>
    </motion.div>
  );
}

export default function ManifestoChapters() {
  return (
    <section data-testid="manifesto-section" className="relative bg-[color:var(--np-bg)] px-6 md:px-10 max-w-[1600px] mx-auto">
      <div className="pt-24 md:pt-40 flex items-center gap-6 mb-16">
        <span className="w-8 h-px bg-[color:var(--np-live)]" />
        <span className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-text-2)]">Manifesto · Five Chapters</span>
      </div>
      {CHAPTERS.map((ch, i) => <Chapter key={ch.n} ch={ch} idx={i} />)}
    </section>
  );
}
